import { useArcadeTickets, useFeeValid } from '@puzzlehq/frontend-utils';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import {
  useGiveawayStats,
  useGiveawayTotalEntriesQuery,
  useGiveawayUserEntriesQuery,
  useGiveawayUtils,
} from '@src/hooks/useGiveaways';
import { AnalyticsEvents, useAnalytics } from '@src/providers/analytics';
import {
  Step,
  useBuyGiveawayStore,
} from '@src/routes/giveaways/$programId/-buyGiveawayStore';
import { RaffleV2, RaffleV2Type } from 'jigsaw';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Button,
  HStack,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
  Spinner,
  Text,
  VStack,
  WarningBanner,
} from 'ui_2';
import { FeeView } from '../FeeBannerView';
import { FAQModal } from '../faq/FAQModal';
import { FAQ_LargestRecord } from '../faq/FAQEntries';
import { AvailabilityCheckbox } from '../purchase/AvailabilityCheckbox';
import { ArcadeTicketIcon } from '../ArcadeTicketIcon';
import { ConnectButton } from '../ConnectOrAccount';
import { GetProButton } from '../pro/GetProButton';
import { trpcReact } from '@src/data/trpc';

export const GiveawayCheckout = ({ giveaway }: { giveaway: RaffleV2 }) => {
  const { account, isConnected, isPro } = useArcadeStoreState();

  const [entries, ticket, status, setEntries, setTicket, sendRequest, setStep] =
    useBuyGiveawayStore((state) => [
      state.entries,
      state.ticket,
      state.status,
      state.setEntries,
      state.setTicket,
      state.sendRequest,
      state.setStep,
    ]);
  const trpcReactUtils = trpcReact.useUtils();

  const [checked, setChecked] = useState(false);

  const minAmount = giveaway ? entries * giveaway.ticketPrice : 0;
  const {
    largestRecordAmount,
    loading,
    smallestFilteredRecord,
    balance: partBalance,
  } = useArcadeTickets(minAmount);

  useEffect(() => {
    smallestFilteredRecord && setTicket(smallestFilteredRecord);
  }, [JSON.stringify(smallestFilteredRecord)]);

  useEffect(() => {
    if (!giveaway) return;
    setEntries(Number(Object.keys(giveaway.entryFunctions)[0] ?? 0));
  }, [JSON.stringify(giveaway)]);

  const { isGiveawayClosed, isGiveawayFull, remainingEntries } =
    useGiveawayUtils(giveaway);

  const hasEnoughTickets =
    largestRecordAmount !== undefined &&
    largestRecordAmount >= entries * giveaway.ticketPrice;
  const isReadyToBuy =
    ticket &&
    entries > 0 &&
    hasEnoughTickets &&
    !isGiveawayClosed &&
    !isGiveawayFull &&
    (giveaway.type === RaffleV2Type.product ? checked : true);
  const entryAmounts = Object.keys(giveaway.entryFunctions);
  const filteredEntryAmounts = entryAmounts.filter(
    (entryTickets) =>
      Number(entryTickets) * giveaway.ticketPrice <= (largestRecordAmount ?? 0)
  );

  const analytics = useAnalytics();

  const feeValid = useFeeValid({
    programId: giveaway.programId,
    functionId: giveaway.entryFunctions[entries.toString()],
  });

  const handleEnterRaffle = async () => {
    void analytics.track(AnalyticsEvents.GiveawaySubmissionStarted, {
      address: account?.address,
      programId: giveaway.programId,
      giveawayId: giveaway.giveawayId,
      entryCount: entries,
      entryPrice: giveaway.ticketPrice,
    });
    if (!giveaway) {
      toast.error('Raffle not found! Go to discord for help.');
      return;
    }
    const response = await sendRequest(giveaway);
    console.log('handleEnterRaffle response', response);
    if (response.error) {
      toast.error(response.error);
    } else if (response.eventId) {
      trpcReactUtils.getUserPendingGiveawayEntries.invalidate();
      setStep(Step._02_Confirmed);
      void analytics.track(AnalyticsEvents.GiveawayEntryPurchaseSubmitted, {
        programId: giveaway.programId,
        giveawayId: giveaway.giveawayId,
        entryCount: entries,
        entryPrice: giveaway.ticketPrice,
      });
    }
  };

  const { data: userEntries, isLoading: loadingUserEntries } =
    useGiveawayUserEntriesQuery(giveaway);
  const { data: totalEntries, isLoading: loadingTotalEntries } =
    useGiveawayTotalEntriesQuery(giveaway);
  const { hasPendingEntries } = useGiveawayStats(giveaway);

  const GiveawayButton = useMemo(() => {
    if (!isConnected) {
      return <ConnectButton />;
    } else if (isPro === false && giveaway.type !== RaffleV2Type.eth) {
      return <GetProButton action='Enter' />;
    } else {
      return (
        <Button
          className='items-center gap-2'
          onClick={async () => await handleEnterRaffle()}
          disabled={!isReadyToBuy || status === 'loading'}
        >
          Enter
          {status === 'loading' && <Spinner />}
        </Button>
      );
    }
  }, [
    status,
    isConnected,
    isPro,
    isReadyToBuy,
    giveaway.type,
    handleEnterRaffle,
  ]);

  return (
    <VStack className='gap-4'>
      <HStack className='w-full gap-4 sm:gap-6'>
        <VStack className='w-1/2 rounded-[5px] border border-gray-100 bg-bg1 px-2.5 py-1.5'>
          <Text font='xs' color='gray-600'>
            Your Entries
          </Text>
          {loadingUserEntries ? (
            <Skeleton className='h-6 w-8' />
          ) : (
            <HStack className='items-center gap-2'>
              <Text>{(userEntries ?? 0).toLocaleString()}</Text>
              {hasPendingEntries && (
                <Spinner className='h-4 w-4 text-gray-700' />
              )}
            </HStack>
          )}
        </VStack>
        <VStack className='w-1/2 rounded-[5px] border border-gray-100 bg-bg1 px-2.5 py-1.5'>
          <Text font='xs' color='gray-600'>
            Total Entries
          </Text>
          {loadingTotalEntries ? (
            <Skeleton className='h-6 w-8' />
          ) : (
            <Text>
              {(totalEntries ?? 0).toLocaleString()}{' '}
              {giveaway.maxEntries
                ? `/ ${giveaway.maxEntries.toLocaleString()}`
                : ''}
            </Text>
          )}
        </VStack>
      </HStack>
      <div className='rounded-lg border border-gray-100 bg-bg1 p-4'>
        <VStack className='h-full gap-4'>
          <TicketSelection
            entries={entries}
            setEntries={setEntries}
            entryAmounts={filteredEntryAmounts}
            ticketPrice={giveaway.ticketPrice}
            remainingEntries={remainingEntries}
            largestRecordAmount={largestRecordAmount}
            loadingPartBalance={loading}
            partBalance={partBalance}
            programId={giveaway.programId}
            functionId={giveaway.entryFunctions[entries.toString()]}
          />
          {isConnected && filteredEntryAmounts.length === 0 && (
            <WarningBanner
              text={
                'Your available Puzzle Arcade Ticket balance is too low to enter into this giveaway.'
              }
            />
          )}
          {isConnected &&
            feeValid === false &&
            (filteredEntryAmounts.length ?? 0) > 0 && (
              <WarningBanner text={'Not enough Aleo!'} />
            )}
          {isConnected && giveaway.type === RaffleV2Type.product && (
            <AvailabilityCheckbox
              checked={checked}
              setChecked={setChecked}
              availableInUserCountry={
                giveaway.productSku.availableInUserCountry
              }
            />
          )}
          {GiveawayButton}
        </VStack>
      </div>
    </VStack>
  );
};

type TicketSelectionProps = {
  entries: number;
  setEntries: (entries: number) => void;
  entryAmounts: string[];
  ticketPrice: number;
  remainingEntries?: number;
  maxEntries?: number;
  largestRecordAmount?: number;
  partBalance?: number;
  loadingPartBalance: boolean;
  programId: string;
  functionId: string;
};

const TicketSelection = ({
  entries,
  setEntries,
  entryAmounts,
  ticketPrice,
  remainingEntries,
  largestRecordAmount,
  loadingPartBalance,
  partBalance,
  programId,
  functionId,
}: TicketSelectionProps) => {
  const { isConnected } = useArcadeStoreState();

  const dropdownEntryAmounts = useMemo(
    () =>
      entryAmounts.filter((amount) =>
        remainingEntries ? Number(amount) <= remainingEntries : true
      ),
    [remainingEntries, entryAmounts]
  );

  return (
    <VStack className='gap-2'>
      <VStack className='gap-1'>
        <HStack className='w-full'>
          {isConnected && <Text font='light-sm'>Your order</Text>}
        </HStack>
        <VStack className='w-full gap-3 rounded-[10px] border border-gray-200 bg-white px-4 py-2 shadow-[0px_4px_4px_0px_#00000040]'>
          {isConnected && (
            <>
              <HStack className='w-full flex-grow items-center'>
                <Text font='regular-sm'># of entries</Text>
                <HStack className='flex-grow' />
                <Select
                  value={entries.toString()}
                  onValueChange={(value) => setEntries(Number(value))}
                >
                  <SelectTrigger
                    className='w-auto'
                    disabled={dropdownEntryAmounts.length === 0}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='max-h-[180px] w-auto' align='end'>
                    {dropdownEntryAmounts.map((entryTickets) => (
                      <SelectItem
                        className='w-auto'
                        key={entryTickets}
                        value={entryTickets}
                      >
                        {entryTickets}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </HStack>
              <Separator className='bg-gray-200' />
            </>
          )}
          <HStack className='w-full flex-grow '>
            <Text font='regular-sm'>Cost</Text>
            <HStack className='flex-grow' />
            <HStack className='items-center gap-2'>
              <ArcadeTicketIcon />
              <Text font='regular-sm'>
                {entries ? `${entries * ticketPrice} PART` : ''}
              </Text>
            </HStack>
          </HStack>
          <FeeView programId={programId} functionId={functionId} />
        </VStack>
      </VStack>
      {isConnected && (
        <VStack className='gap-1'>
          <Text font='light-sm'>Your PART</Text>
          <HStack className='w-full flex-grow items-center gap-2 rounded-[10px] border border-gray-200 bg-white px-4 py-2 shadow-[0px_4px_4px_0px_#00000040]'>
            {loadingPartBalance ? (
              <Skeleton className='h-6 w-12' />
            ) : (
              <>
                <ArcadeTicketIcon />
                <Text font='regular-sm'>
                  {largestRecordAmount?.toLocaleString() ?? 0} available
                </Text>
                <FAQModal entry={FAQ_LargestRecord} />
              </>
            )}
            <HStack className='flex-grow' />
            {loadingPartBalance ? (
              <Skeleton className='h-6 w-12' delay={1} />
            ) : (
              <Text color='gray-500' font='regular-sm'>
                {partBalance?.toLocaleString() ?? 0} total
              </Text>
            )}
          </HStack>
        </VStack>
      )}
    </VStack>
  );
};
